import { Button, Icon, Typography } from '@smartgift/frontend-toolkit';
import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { Card, CardContent, CardHeader } from '@hero/atoms';
import { DownloadReport } from '@hero/components/modals';
import { DetailItem, OrderStatusBadge, Table } from '@hero/molecules';
import { ROUTE_PATHS } from '@hero/routes';
import { useGetAutomation } from '@hero/services';
import { useThankYouMessagesReport } from '@hero/services/analytic-service/hooks';
import { useGetThankYouNotes } from '@hero/services/order-service';
import { getName, getSafeInnerHtml } from '@hero/utils';

import * as S from './style';

function AutomationThankYouNotesTable() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { code } = useParams();
  const [selectedNote, setSelectedNote] = useState(null);
  const [{ searchTerm }, setGlobalFilters] = useState({});
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);

  const [{ pageIndex, pageSize }, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10
  });

  const { data: automation } = useGetAutomation(
    { code },
    {
      enabled: !!code
    }
  );

  const automationId = automation?.id;

  const { data: thankYouNotesData, isLoading: thankYouNotesLoading } = useGetThankYouNotes(
    {
      automationId,
      page: pageIndex,
      searchTerm
    },
    {
      enabled: !!automationId
    }
  );

  const { mutateAsync: generateTYMessagesReport } = useThankYouMessagesReport();

  const columns = React.useMemo(
    () => [
      {
        header: t('name'),
        accessorKey: 'recipient',
        cell: ({ cell }) => {
          const value = cell?.getValue();
          return <S.RecipientName>{getName(value)}</S.RecipientName>;
        }
      },
      {
        header: t('tyNote'),
        accessorKey: 'experience',
        cell: ({ cell }) => (
          <Typography
            variant="body2"
            {...getSafeInnerHtml(cell.getValue()?.thankYouMessage)}
            lineClamp={1}
          />
        )
      }
    ],
    [thankYouNotesData]
  );

  return (
    <S.TYNotesWrapper>
      <S.ActionHeader>
        <Button
          variant="text"
          startIcon={<Icon name="download" />}
          onClick={() => setIsDownloadModalOpen(true)}
          disabled={thankYouNotesData?.docs?.length === 0}
        >
          {t('downloadList')}
        </Button>
      </S.ActionHeader>
      <S.InfoHeader>
        <Typography variant="body2">
          <Trans
            i18nKey="total"
            values={{
              count: thankYouNotesData?.docs?.length
            }}
            components={{
              b: <b />
            }}
          />
        </Typography>
      </S.InfoHeader>
      <S.TableContainer>
        <Table
          columns={columns}
          data={thankYouNotesData?.docs || []}
          loading={thankYouNotesLoading}
          pageCount={thankYouNotesData?.totalPages}
          pagination={{
            pageIndex,
            pageSize,
            total: thankYouNotesData?.totalDocs
          }}
          manualPagination
          setPagination={setPagination}
          setGlobalFilters={setGlobalFilters}
          useGlobalFuzzyFilter
          rowHeight={52}
          onRowClick={row => setSelectedNote(row.original)}
          enabled={{
            filter: false,
            header: true
          }}
        />
        {selectedNote ? (
          <S.MessageCardWrapper>
            <Card style={{ padding: 16 }}>
              <CardHeader style={{ padding: 0 }}>
                <Typography variant="body1Bold">
                  {getName({
                    firstName: selectedNote?.recipient?.firstName,
                    lastName: selectedNote?.recipient?.lastName
                  })}
                </Typography>
                <OrderStatusBadge status={selectedNote?.giftStatus} />
              </CardHeader>
              <CardContent style={{ marginTop: 16 }}>
                <Button
                  onClick={() => {
                    navigate(ROUTE_PATHS.AUTOMATION_RECIPIENT_DETAIL(code, selectedNote?.code));
                  }}
                  startIcon={<Icon name="contacts" />}
                  variant="outlined"
                  block
                  style={{ marginBottom: 16 }}
                >
                  {t('viewContactDetails').toUpperCase()}
                </Button>

                <DetailItem title={`${t('email')}:`} withBorder>
                  {selectedNote.recipient?.email}
                </DetailItem>
                <DetailItem title={`${t('tyNote')}:`} withBorder>
                  <div {...getSafeInnerHtml(selectedNote.experience?.thankYouMessage)} />
                </DetailItem>
                <DetailItem title={`${t('giftMessage')}:`}>
                  {selectedNote.experience?.giftMessage && (
                    <div {...getSafeInnerHtml(selectedNote.experience?.giftMessage)} />
                  )}
                </DetailItem>
              </CardContent>
            </Card>
          </S.MessageCardWrapper>
        ) : (
          <S.InitialPreview>
            <Typography variant="body3Italic" color="gray">{`(${t(
              'thankYouNotesPreviewSubTitle'
            )})`}</Typography>
          </S.InitialPreview>
        )}
      </S.TableContainer>
      {isDownloadModalOpen && (
        <DownloadReport
          isOpen={isDownloadModalOpen}
          onRequestClose={setIsDownloadModalOpen}
          onAsyncSubmit={async data => {
            await generateTYMessagesReport({
              emails: data.contacts.map(item => item.email),
              filters: {
                associationType: 'automation',
                associationCode: automation.code
              }
            });
          }}
        />
      )}
    </S.TYNotesWrapper>
  );
}

export { AutomationThankYouNotesTable };
